import React, { FC, useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Chip } from '@material-ui/core'
import ChipInput from 'material-ui-chip-input'
import KeywordChipList from './KeywordChipList'
import { hiraToKana, kanaToHira } from '../../../utils/string'
import { ReactSVG } from 'react-svg'

interface Props {
  placeHolder: string
  team: {
    modalName: string
    options: string[] | undefined
    keywords: string[]
    selectLimit: number
    displayLimit: number
    setKeywords: (keyword: string[]) => void
  }
  member: {
    modalName: string
    options: string[] | undefined
    keywords: string[]
    selectLimit: number
    displayLimit: number
    setKeywords: (keyword: string[]) => void
  }
  resetKeywords: () => void
}

interface Keyword {
  type: string
  word: string
  value: string
  checked: boolean
  displayed: boolean
}

const TeamAndMemberKeywordInput: FC<Props> = (props) => {
  const {
    placeHolder,
    team,
    member,
    resetKeywords,
  } = props

  const KEYWORD_DISPLAY_LIMIT = team.displayLimit

  const [keywordList, setKeywordList] = useState<Keyword[]>([])
  const [keywordErrorMessage, setKeywordErrorMessage] = useState('')
  const [isKeywordDisplay, setIsKeywordDisplay] = useState(false)
  const [keywordHintMessage, setKeywordHintMessage] = useState('')
  const [tmpKeyword, setTmpKeyword] = useState<string>('')

  const [selectedKeywordList, setSelectedKeywordList] = useState<string[]>([])

  const synchronizeKeywordListChip = (keyword: string | number, deleteMode = false, clearMode = false) => {
    const updateList = keywordList.map((value) => {
      if (value.value === keyword) {
        return { ...value, checked: !deleteMode }
      }
      if (clearMode) {
        return { ...value, displayed: true }
      }
      return value
    })
    setKeywordList(updateList)
  }

  const addTeamKeyword = (keyword: string, clearMode = false) => {
    setKeywordErrorMessage('')
    team.setKeywords([...team.keywords, keyword])
    synchronizeKeywordListChip(keyword, false, clearMode)
    setTmpKeyword('')
  }

  const addMemberKeyword = (keyword: string, clearMode = false) => {
    setKeywordErrorMessage('')
    member.setKeywords([...member.keywords, keyword])
    synchronizeKeywordListChip(keyword, false, clearMode)
    setTmpKeyword('')
  }

  const resetKeywordList = () => {
    const updateList = keywordList.map((value, index: number) => (
      { ...value, checked: selectedKeywordList.includes(value.word), displayed: (index < KEYWORD_DISPLAY_LIMIT) }))
    setKeywordList(updateList)
  }

  const resetKeywordChip = () => {
    const updateList = keywordList.map((value) => ({ ...value, checked: false }))
    setKeywordList(updateList)
  }

  const closeKeywordModal = () => {
    setTmpKeyword('')
    setIsKeywordDisplay(false)
    resetKeywordList()
  }

  const deleteKeyword = (keyword: string) => {
    if (team.keywords.includes(keyword)) {
      team.setKeywords(team.keywords.filter((val) => val !== keyword))
    } else {
      member.setKeywords(member.keywords.filter((val) => val !== keyword))
    }
    synchronizeKeywordListChip(keyword, true)
    setKeywordHintMessage('')
  }

  const keywordSelect = (word: string) => {
    let count = 0
    const lowerCasedWord = word.toLowerCase()
    const hiragana = kanaToHira(word)
    const katakana = hiraToKana(word)
    const updateList = keywordList.map((item) => {
      if (
        (
          (item.word.toLowerCase().indexOf(lowerCasedWord) > -1 || item.word.indexOf(hiragana) > -1 || item.word.indexOf(katakana) > -1)
          && count < KEYWORD_DISPLAY_LIMIT
        )
      ) {
        count += 1
        return { ...item, displayed: true }
      }
      return { ...item, displayed: false }
    })
    setKeywordList(updateList)
    setTmpKeyword(word)
  }

  const keywordSuggest = (e: React.FormEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value.trim()
    if (inputValue === "") {
      resetKeywordList()
      setTmpKeyword('')
      return
    }
    setIsKeywordDisplay(true)
    keywordSelect(inputValue)
  }

  const openKeywordModal = () => {
    setIsKeywordDisplay(true)
    resetKeywordList()
  }

  const resetKeyword = () => {
    resetKeywords()
    resetKeywordChip()
    setKeywordHintMessage('')
  }

  const choiceFirstChip = (keyCode: number) => {
    const tmpKeywordList = keywordList.filter((keyword) => keyword.displayed && !keyword.checked)
    if (keyCode !== 13 || !tmpKeywordList.length || !tmpKeyword) return
    if (tmpKeywordList[0].type === "team") {
      addTeamKeyword(tmpKeywordList[0].word, true)
    } else {
      addMemberKeyword(tmpKeywordList[0].word, true)
    }
  }

  useEffect(() => {
    setSelectedKeywordList([...team.keywords, ...member.keywords])
  }, [team.keywords, member.keywords])

  useEffect(() => {
    const team_res = team.options
      ? team.options.map((option: string) => ({ type: "team", word: option, value: option, checked: false, displayed: true }))
      : []
    const member_res = member.options
      ? member.options.map((option: string) => ({ type: "member", word: option, value: option, checked: false, displayed: true }))
      : []
    const res = [...team_res, ...member_res]
    setKeywordList(res)
  }, [])

  return (
    <div>
      <div className="input_field">
        <ReactSVG
          src="/static/images/icon/icon-search.svg"
          className="icon"
        />
        <ChipInput
          id="keywords"
          value={selectedKeywordList}
          placeholder={placeHolder}
          onUpdateInput={keywordSuggest}
          onClick={openKeywordModal}
          newChipKeyCodes={[]}
          newChipKeys={[""]}
          onFocus={openKeywordModal}
          onKeyDown={(key) => choiceFirstChip(key.keyCode)}
          inputValue={tmpKeyword}
          chipRenderer={({ value }, key) => (
            <Chip
              className="chip no_trans"
              key={key}
              label={value}
              variant="outlined"
              onDelete={() => deleteKeyword(value)}
              deleteIcon={<div>×</div>}
            />
          )}
        />
        {
          selectedKeywordList.length !== 0 && (
            <button type="button" className="reset" onClick={() => resetKeyword()}>
              ×
            </button>
          )
        }
      </div>
      <div>
        {
          isKeywordDisplay && (
            <>
              <div className="suggest_box" role="none" onClick={() => setTmpKeyword('')}>
                <div className="suggest_contents">
                  <KeywordChipList
                    modalName={team.modalName}
                    keywordList={keywordList.filter((val) => val.type === "team")}
                    addKeyword={addTeamKeyword}
                  />
                  <KeywordChipList
                    modalName={member.modalName}
                    keywordList={keywordList.filter((val) => val.type === "member")}
                    addKeyword={addMemberKeyword}
                  />
                </div>
                <button type="button" className="closebox" onClick={closeKeywordModal}>
                  <FontAwesomeIcon icon={['far', 'times-circle']} />
                </button>
              </div>
              <div className="search_screen" role="presentation" onClick={closeKeywordModal} />
            </>
          )
        }
      </div>
      {
        keywordErrorMessage && (
          <div className="error_field">
            {keywordErrorMessage}
          </div>
        )
      }
      {
        keywordHintMessage && (
          <div className="hint_field">
            {keywordHintMessage}
          </div>
        )
      }
    </div>
  )
}

export default TeamAndMemberKeywordInput
